import { useEffect } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { chatsUnreadCountQueryKey } from './chatsUnreadCount';

/**
 * `purpose=unread_badge` tells Hami this socket only listens for the badge push: it does not join
 * Hami's presence (no user_online/user_offline fan-out to every Hami socket) and gets no broadcasts.
 * Without it every page view here was a Hami "login" broadcast to all of Hami (2026-11-01 storm).
 */
export const HAMI_WS_URL = 'wss://hami-back.paziresh24.com/ws?purpose=unread_badge';
const HEARTBEAT_INTERVAL_MS = 25_000;
/** Page navigations remount the bottom nav, and users flip apps all the time: keep the socket that long. */
export const CLOSE_GRACE_MS = 60_000;
/** Never open a new socket sooner than this after the last one opened (a flapping network or tab). */
export const MIN_REOPEN_GAP_MS = 30_000;

/**
 * One socket per browser tab, shared by every mounted bottom nav. Earlier each mount owned its own
 * socket, so every page navigation (the nav lives inside page layouts) closed and reopened it.
 */
const state: {
  socket: WebSocket | null;
  heartbeat: ReturnType<typeof setInterval> | null;
  closeTimer: ReturnType<typeof setTimeout> | null;
  reopenTimer: ReturnType<typeof setTimeout> | null;
  users: number;
  lastOpenAt: number;
  queryClient: QueryClient | null;
  visibilityBound: boolean;
} = {
  socket: null,
  heartbeat: null,
  closeTimer: null,
  reopenTimer: null,
  users: 0,
  lastOpenAt: -Infinity,
  queryClient: null,
  visibilityBound: false,
};

const isVisible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';

const clearTimer = (key: 'closeTimer' | 'reopenTimer') => {
  const timer = state[key];
  if (timer) clearTimeout(timer);
  state[key] = null;
};

const closeSocket = () => {
  clearTimer('reopenTimer');
  if (state.heartbeat) clearInterval(state.heartbeat);
  state.heartbeat = null;
  const socket = state.socket;
  state.socket = null;
  if (socket) {
    try {
      socket.close();
    } catch {
      // ignore
    }
  }
};

const openSocket = () => {
  state.lastOpenAt = Date.now();
  const socket = new WebSocket(HAMI_WS_URL);
  state.socket = socket;

  socket.onopen = () => {
    if (state.heartbeat) clearInterval(state.heartbeat);
    state.heartbeat = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'heartbeat' }));
        } catch {
          // ignore
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  socket.onmessage = event => {
    try {
      const data = JSON.parse(event.data);
      if (data?.type === 'unread_count_update') {
        const total = Number(data.total);
        if (Number.isFinite(total)) {
          state.queryClient?.setQueryData(chatsUnreadCountQueryKey, Math.max(0, total));
        }
      }
    } catch {
      // ignore malformed frames
    }
  };

  const onSocketClosed = () => {
    if (state.socket !== socket) return;
    if (state.heartbeat) clearInterval(state.heartbeat);
    state.heartbeat = null;
    state.socket = null;
    // No reconnect loop: the two-minute poll covers the badge until the next mount or tab show.
  };
  socket.onclose = onSocketClosed;
  socket.onerror = onSocketClosed;
};

const ensureOpen = () => {
  clearTimer('closeTimer');
  if (state.users <= 0 || !isVisible() || state.socket || state.reopenTimer) return;
  const wait = state.lastOpenAt + MIN_REOPEN_GAP_MS - Date.now();
  if (wait > 0) {
    state.reopenTimer = setTimeout(() => {
      state.reopenTimer = null;
      ensureOpen();
    }, wait);
    return;
  }
  openSocket();
};

const scheduleClose = () => {
  clearTimer('reopenTimer');
  if (state.closeTimer || !state.socket) return;
  state.closeTimer = setTimeout(() => {
    state.closeTimer = null;
    if (state.users <= 0 || !isVisible()) closeSocket();
  }, CLOSE_GRACE_MS);
};

const onVisibilityChange = () => {
  if (isVisible()) ensureOpen();
  else scheduleClose();
};

/**
 * Live push for the chats-unread badge (hami/hami-backend!169's `unread_count_update`).
 * Only while `enabled` (login + mobile, same gate as the polling fallback in `useChatsUnreadCount`)
 * and the tab is visible; a hidden tab or the last unmount closes it after CLOSE_GRACE_MS.
 * The polling fallback stays authoritative: a dropped socket only means the badge waits for the poll.
 */
export const useChatsUnreadLiveSocket = (enabled: boolean) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    state.queryClient = queryClient;
    state.users += 1;
    if (!state.visibilityBound) {
      document.addEventListener('visibilitychange', onVisibilityChange);
      state.visibilityBound = true;
    }
    ensureOpen();

    return () => {
      state.users = Math.max(0, state.users - 1);
      if (state.users === 0) scheduleClose();
    };
  }, [enabled, queryClient]);
};

/** Test-only: forget the shared socket between tests. */
export const resetChatsUnreadLiveSocketForTests = () => {
  clearTimer('closeTimer');
  closeSocket();
  state.users = 0;
  state.lastOpenAt = -Infinity;
  state.queryClient = null;
};
